import { LoadingState, Alert } from '../ui';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import SidebarNavbar from "../components/SidebarNavbar";
import BottomNavbar from "../components/BottomNavbar";
import notify from "../lib/notify";
import { Button as FcButton, Card as FcCard, Input as FcInput, Select as FcSelect, Table as FcTable } from '../ui';
import { Dialog as FcDialog } from '../ui';
import { Tabs as FcTabs } from '../ui';
import { ScoreControl, StatusBadge } from '../ui';

const STAGE_FILTER_ORDER = [
  "GROUP",
  "ROUND_OF_32",
  "ROUND_OF_16",
  "QUARTER_FINAL",
  "SEMI_FINAL",
  "FINAL",
  "THIRD_PLACE",
];

const STAGE_LABELS = {
  GROUP: "Group Stage",
  ROUND_OF_32: "Round of 32",
  ROUND_OF_16: "Round of 16",
  QUARTER_FINAL: "Quarter-final",
  SEMI_FINAL: "Semi-final",
  FINAL: "Final",
  THIRD_PLACE: "Third-place playoff",
};

const GROUP_LETTERS = "ABCDEFGHIJKL".split("");

const getGroupStageNumber = (stageValue) => {
  const stage = String(stageValue || "").trim();
  if (!/^group stage\b/i.test(stage)) return null;

  const match = stage.match(/group stage\s*(?:[-:]\s*)?([123])\b/i);
  return match?.[1] || null;
};

const getExplicitGroupLetter = (stageValue) => {
  const match = String(stageValue || "").match(/\bgroup\s+([A-L])\b/i);
  return match?.[1]?.toUpperCase() || null;
};

const isConfirmedTeamName = (teamName) => {
  const t = (teamName || "").trim().toLowerCase();
  if (!t) return false;
  if (
    t.startsWith("tbc") ||
    t.startsWith("to be confirmed") ||
    t.startsWith("winner ") ||
    t.startsWith("runner-up ") ||
    t.startsWith("runner up ")
  ) {
    return false;
  }
  return true;
};

const getFixtureLockDeadline = (kickoffTime) => {
  if (!kickoffTime) return null;
  return new Date(new Date(kickoffTime).getTime() - 60 * 60 * 1000);
};

const normalizeStageFilterKey = (stageValue) => {
  const s = String(stageValue || "").trim().toLowerCase();
  if (!s) return null;

  if (s.startsWith("group stage") || s === "group") return "GROUP";
  if (s === "round_of_32" || s === "round of 32" || s === "round of32") return "ROUND_OF_32";
  if (s === "round_of_16" || s === "round of 16" || s === "round of16") return "ROUND_OF_16";
  if (s.startsWith("quarter") || s === "quarter_final" || s === "quarter-final") return "QUARTER_FINAL";
  if (s.startsWith("semi") || s === "semi_final" || s === "semi-final") return "SEMI_FINAL";
  if (
    s === "third_place" ||
    s === "third place" ||
    s === "third-place playoff" ||
    s === "3rd place final" ||
    s === "third place final"
  ) {
    return "THIRD_PLACE";
  }
  if (s === "final") return "FINAL";

  return String(stageValue || "").trim().toUpperCase().replace(/\s+/g, "_");
};

const getPredictionStageKey = (stageValue) => {
  const groupStageNumber = getGroupStageNumber(stageValue);
  if (groupStageNumber) return `GROUP_STAGE_${groupStageNumber}`;
  return normalizeStageFilterKey(stageValue);
};

const getPredictionStageLabel = (stageKey) => {
  const groupStageMatch = String(stageKey || "").match(/^GROUP_STAGE_([123])$/);
  if (groupStageMatch) return `Group Stage ${groupStageMatch[1]}`;
  return STAGE_LABELS[stageKey] || String(stageKey || "").replace(/_/g, " ");
};

export default function WorldStage({ session, userRole }) {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "predictions");
  const [competition, setCompetition] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [formValues, setFormValues] = useState({});
  const [isRegistered, setIsRegistered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stageFilter, setStageFilter] = useState("OVERALL");
  const [predictionStageFilter, setPredictionStageFilter] = useState("ALL");
  const [predictionGroupFilter, setPredictionGroupFilter] = useState("ALL");
  const [fixtureSyncStatus, setFixtureSyncStatus] = useState({});
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [teamLogoMap, setTeamLogoMap] = useState({});
  const [teamDisplayNameMap, setTeamDisplayNameMap] = useState({});
  const [showUnregisterConfirm, setShowUnregisterConfirm] = useState(false);
  const [showPrivateKeyModal, setShowPrivateKeyModal] = useState(false);
  const [privateRegistrationKey, setPrivateRegistrationKey] = useState("");
  const [error, setError] = useState("");
  const syncDebounceRef = useRef({});
  const syncRequestSeqRef = useRef({});

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", activeTab);
      return next;
    });
  }, [activeTab, setSearchParams]);

  useEffect(() => {
    return () => {
      Object.values(syncDebounceRef.current).forEach((timerId) => clearTimeout(timerId));
    };
  }, []);

  const loadPredictions = async (userId, compId) => {
    const { data: entry, error: entryError } = await supabase
      .from("world_stage_entries")
      .select("id")
      .eq("competition_id", compId)
      .eq("user_id", userId)
      .maybeSingle();

    if (entryError) throw entryError;

    if (!entry?.id) {
      setPredictions({});
      setFormValues({});
      return;
    }

    const { data: rows, error: predictionError } = await supabase
      .from("world_stage_predictions")
      .select("fixture_id, predicted_home_goals, predicted_away_goals, points_awarded, exact, result_correct")
      .eq("world_stage_entry_id", entry.id);

    if (predictionError) throw predictionError;

    const mapped = {};
    const forms = {};
    (rows || []).forEach((r) => {
      mapped[r.fixture_id] = r;
      forms[r.fixture_id] = {
        home: String(r.predicted_home_goals),
        away: String(r.predicted_away_goals),
      };
    });

    setPredictions(mapped);
    setFormValues(forms);
  };

  const getTeamLogo = (teamName) => {
    const key = String(teamName || "").trim().toLowerCase();
    if (!key) return "";
    return teamLogoMap[key] || "";
  };

  const getTeamDisplayName = (teamName) => {
    const key = String(teamName || "").trim().toLowerCase();
    if (!key) return teamName || "";
    return teamDisplayNameMap[key] || teamName;
  };

  const formatFixtureKickoff = (kickoffTime) => {
    if (!kickoffTime) return "No kickoff time";
    return new Date(kickoffTime).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const user = session?.user;
        if (!user?.id) {
          navigate("/login");
          return;
        }

        const { data: comp, error: compError } = await supabase
          .from("competitions")
          .select("id, name, type, league, season, status, visibility, registration_status, start_match_day")
          .eq("id", competitionId)
          .maybeSingle();

        if (compError) throw compError;
        if (!comp) {
          setError("Competition not found.");
          return;
        }
        if (comp.type !== "world_stage") {
          setError("This competition is not a World Stage competition.");
          return;
        }

        setCompetition(comp);

        const { data: registration } = await supabase
          .from("user_selections")
          .select("id")
          .eq("competition_id", competitionId)
          .eq("user_id", user.id)
          .maybeSingle();

        setIsRegistered(!!registration?.id);

        const { data: fixtureRows, error: fixtureError } = await supabase
          .from("fixtures")
          .select("id, stage, kickoff_time, home_team, away_team, home_score, away_score, status")
          .eq("league", comp.league)
          .eq("season", comp.season)
          .order("kickoff_time", { ascending: true });

        if (fixtureError) throw fixtureError;
        setFixtures(fixtureRows || []);

        const { data: teamRows } = await supabase
          .from("teams")
          .select("display_name, official_name, footystats_name, logo_url")
          .not("logo_url", "is", null);

        const logos = {};
        const displayNames = {};
        (teamRows || []).forEach((team) => {
          const preferredDisplay = team.display_name || team.official_name || team.footystats_name;
          const names = [team.display_name, team.official_name, team.footystats_name];
          names.forEach((name) => {
            const key = String(name || "").trim().toLowerCase();
            if (key) {
              if (team.logo_url) {
                logos[key] = team.logo_url;
              }
              if (preferredDisplay) {
                displayNames[key] = preferredDisplay;
              }
            }
          });
        });
        setTeamLogoMap(logos);
        setTeamDisplayNameMap(displayNames);

        await loadPredictions(user.id, competitionId);
      } catch (e) {
        setError(e.message || "Failed to load World Stage");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [competitionId, navigate, session]);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      if (activeTab !== "leaderboard") return;
      if (!competitionId) return;

      try {
        setLeaderboardLoading(true);
        const stageArg = stageFilter === "OVERALL" ? null : stageFilter;
        const { data, error: leaderboardError } = await supabase.rpc("get_world_stage_leaderboard", {
          p_competition_id: competitionId,
          p_stage: stageArg,
        });

        if (leaderboardError) throw leaderboardError;
        setLeaderboard(data || []);
      } catch (e) {
        notify(e.message || "Failed to load leaderboard", "error");
      } finally {
        setLeaderboardLoading(false);
      }
    };

    fetchLeaderboard();
  }, [activeTab, competitionId, stageFilter]);

  const predictionStageOptions = useMemo(() => {
    const availableStageKeys = new Set(fixtures.map((fixture) => getPredictionStageKey(fixture.stage)).filter(Boolean));
    const preferredOrder = [
      "GROUP_STAGE_1",
      "GROUP_STAGE_2",
      "GROUP_STAGE_3",
      ...STAGE_FILTER_ORDER.filter((stageKey) => stageKey !== "GROUP"),
    ];
    const orderedKeys = preferredOrder.filter((stageKey) => availableStageKeys.delete(stageKey));
    const remainingKeys = Array.from(availableStageKeys).sort();

    return [
      { key: "ALL", label: "All Stages" },
      ...[...orderedKeys, ...remainingKeys].map((stageKey) => ({
        key: stageKey,
        label: getPredictionStageLabel(stageKey),
      })),
    ];
  }, [fixtures]);

  const fixtureGroupMap = useMemo(() => {
    const groupStageFixtures = fixtures.filter((fixture) => normalizeStageFilterKey(fixture.stage) === "GROUP");
    const teamGraph = new Map();
    const explicitTeamGroups = new Map();

    const addTeam = (teamName) => {
      if (!teamGraph.has(teamName)) teamGraph.set(teamName, new Set());
    };

    groupStageFixtures.forEach((fixture) => {
      const homeTeam = isConfirmedTeamName(fixture.home_team) ? fixture.home_team.trim().toLowerCase() : null;
      const awayTeam = isConfirmedTeamName(fixture.away_team) ? fixture.away_team.trim().toLowerCase() : null;
      const explicitGroup = getExplicitGroupLetter(fixture.stage);

      if (homeTeam) addTeam(homeTeam);
      if (awayTeam) addTeam(awayTeam);
      if (homeTeam && awayTeam) {
        teamGraph.get(homeTeam).add(awayTeam);
        teamGraph.get(awayTeam).add(homeTeam);
      }
      if (explicitGroup) {
        if (homeTeam) explicitTeamGroups.set(homeTeam, explicitGroup);
        if (awayTeam) explicitTeamGroups.set(awayTeam, explicitGroup);
      }
    });

    const visitedTeams = new Set();
    const components = [];

    teamGraph.forEach((_, startingTeam) => {
      if (visitedTeams.has(startingTeam)) return;

      const teams = [];
      const queue = [startingTeam];
      visitedTeams.add(startingTeam);

      while (queue.length > 0) {
        const team = queue.shift();
        teams.push(team);
        teamGraph.get(team)?.forEach((opponent) => {
          if (!visitedTeams.has(opponent)) {
            visitedTeams.add(opponent);
            queue.push(opponent);
          }
        });
      }

      const componentFixtures = groupStageFixtures.filter((fixture) => {
        const homeTeam = String(fixture.home_team || "").trim().toLowerCase();
        const awayTeam = String(fixture.away_team || "").trim().toLowerCase();
        return teams.includes(homeTeam) || teams.includes(awayTeam);
      });
      const firstFixtureIndex = Math.min(...componentFixtures.map((fixture) => fixtures.indexOf(fixture)));
      const explicitGroup = teams.map((team) => explicitTeamGroups.get(team)).find(Boolean) || null;
      components.push({ teams, firstFixtureIndex, explicitGroup });
    });

    components.sort((a, b) => a.firstFixtureIndex - b.firstFixtureIndex);
    const usedGroups = new Set(components.map((component) => component.explicitGroup).filter(Boolean));
    const availableGroups = GROUP_LETTERS.filter((letter) => !usedGroups.has(letter));
    let nextAvailableGroup = 0;
    const teamGroups = new Map();

    components.forEach((component) => {
      const group = component.explicitGroup || availableGroups[nextAvailableGroup++];
      component.teams.forEach((team) => teamGroups.set(team, group));
    });

    return Object.fromEntries(
      groupStageFixtures.map((fixture) => {
        const explicitGroup = getExplicitGroupLetter(fixture.stage);
        const homeTeam = String(fixture.home_team || "").trim().toLowerCase();
        const awayTeam = String(fixture.away_team || "").trim().toLowerCase();
        return [fixture.id, explicitGroup || teamGroups.get(homeTeam) || teamGroups.get(awayTeam) || null];
      })
    );
  }, [fixtures]);

  const filteredFixtures = useMemo(
    () =>
      fixtures.filter((fixture) => {
        const matchesStage =
          predictionStageFilter === "ALL" || getPredictionStageKey(fixture.stage) === predictionStageFilter;
        const matchesGroup =
          predictionGroupFilter === "ALL" || fixtureGroupMap[fixture.id] === predictionGroupFilter;
        return matchesStage && matchesGroup;
      }),
    [fixtureGroupMap, fixtures, predictionGroupFilter, predictionStageFilter]
  );

  const groupedFixtures = useMemo(() => {
    const grouped = {};
    filteredFixtures.forEach((f) => {
      const key = f.kickoff_time
        ? new Date(f.kickoff_time).toLocaleDateString(undefined, {
            weekday: "short",
            day: "numeric",
            month: "short",
          })
        : "No kickoff date";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(f);
    });
    return grouped;
  }, [filteredFixtures]);

  useEffect(() => {
    if (!predictionStageOptions.some((option) => option.key === predictionStageFilter)) {
      setPredictionStageFilter("ALL");
    }
  }, [predictionStageFilter, predictionStageOptions]);

  const leaderboardStageOptions = useMemo(() => {
    const completedStageKeys = new Set(
      fixtures
        .filter((f) => f.status === "complete")
        .map((f) => normalizeStageFilterKey(f.stage))
        .filter(Boolean)
    );

    const stageOptions = STAGE_FILTER_ORDER.filter((stageKey) => completedStageKeys.has(stageKey)).map((stageKey) => ({
      key: stageKey,
      label: STAGE_LABELS[stageKey] || stageKey,
    }));

    return [{ key: "OVERALL", label: "Overall" }, ...stageOptions];
  }, [fixtures]);

  useEffect(() => {
    if (!leaderboardStageOptions.some((option) => option.key === stageFilter)) {
      setStageFilter("OVERALL");
    }
  }, [leaderboardStageOptions, stageFilter]);

  const parseScore = (value) => {
    if (value === "" || value == null) return null;
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : null;
  };

  const getScoreDisplay = (value) => {
    const parsed = parseScore(value);
    return parsed == null ? "-" : String(parsed);
  };

  const getSyncLabel = (status) => {
    switch (status) {
      case "syncing":
        return "Syncing...";
      case "synced":
        return "Synced";
      case "unsynced":
        return "Unsynced";
      case "error":
        return "Sync failed";
      default:
        return "Sync";
    }
  };

  const schedulePredictionSync = (fixture, homeRaw, awayRaw) => {
    const home = parseScore(homeRaw);
    const away = parseScore(awayRaw);

    if (home == null || away == null) {
      setFixtureSyncStatus((prev) => ({ ...prev, [fixture.id]: "unsynced" }));
      return;
    }

    const seq = (syncRequestSeqRef.current[fixture.id] || 0) + 1;
    syncRequestSeqRef.current[fixture.id] = seq;

    if (syncDebounceRef.current[fixture.id]) {
      clearTimeout(syncDebounceRef.current[fixture.id]);
    }

    setFixtureSyncStatus((prev) => ({ ...prev, [fixture.id]: "syncing" }));

    syncDebounceRef.current[fixture.id] = setTimeout(async () => {
      try {
        const { error: saveError } = await supabase.rpc("upsert_world_stage_prediction", {
          p_competition_id: competitionId,
          p_fixture_id: fixture.id,
          p_home_goals: home,
          p_away_goals: away,
        });

        if (syncRequestSeqRef.current[fixture.id] !== seq) return;
        if (saveError) throw saveError;

        setPredictions((prev) => ({
          ...prev,
          [fixture.id]: {
            ...(prev[fixture.id] || {}),
            fixture_id: fixture.id,
            predicted_home_goals: home,
            predicted_away_goals: away,
          },
        }));
        setFixtureSyncStatus((prev) => ({ ...prev, [fixture.id]: "synced" }));
      } catch (_e) {
        if (syncRequestSeqRef.current[fixture.id] !== seq) return;
        setFixtureSyncStatus((prev) => ({ ...prev, [fixture.id]: "error" }));
      }
    }, 350);
  };

  const stepScore = (fixture, side, delta) => {
    setFormValues((prev) => {
      const fixtureValues = prev[fixture.id] || {};
      const current = parseScore(fixtureValues[side]);
      let next = current;

      if (current == null) {
        if (delta > 0) {
          next = 1;
        } else if (delta < 0) {
          next = 0;
        }
      } else {
        next = Math.min(20, Math.max(0, current + delta));
      }

      const nextValue = next == null ? "" : String(next);
      const nextFixtureValues = {
        ...fixtureValues,
        [side]: nextValue,
      };

      schedulePredictionSync(
        fixture,
        side === "home" ? nextValue : (nextFixtureValues.home || ""),
        side === "away" ? nextValue : (nextFixtureValues.away || "")
      );

      return {
        ...prev,
        [fixture.id]: nextFixtureValues,
      };
    });
  };

  const handleRegister = async () => {
    if (!session?.user?.id || !competition) {
      notify("You must be logged in.", "info");
      return;
    }

    if (competition.registration_status !== "open") {
      notify("Registration is closed for this competition.", "error");
      return;
    }

    if (competition.visibility === "private") {
      setShowPrivateKeyModal(true);
      return;
    }

    try {
      setRegistrationLoading(true);
      const { error: joinError } = await supabase.from("user_selections").insert([
        {
          user_id: session.user.id,
          competition_id: competition.id,
          league: competition.league,
          season: competition.season,
          match_day: competition.start_match_day,
        },
      ]);

      if (joinError) {
        const alreadyRegistered =
          joinError.code === "23505" || (joinError.message || "").toLowerCase().includes("duplicate key");
        if (alreadyRegistered) {
          setIsRegistered(true);
          notify("You are already registered for this competition.", "info");
          return;
        }
        throw joinError;
      }

      setIsRegistered(true);
      notify("Successfully registered.", "success");
    } catch (e) {
      notify(e.message || "Failed to register.", "error");
    } finally {
      setRegistrationLoading(false);
    }
  };

  const handleRegisterWithPrivateKey = async () => {
    if (!session?.user?.id || !competition) {
      notify("You must be logged in.", "info");
      return;
    }

    const key = (privateRegistrationKey || "").trim().toUpperCase();
    if (!key) {
      notify("Please enter a registration key.", "info");
      return;
    }

    try {
      setRegistrationLoading(true);

      const { data: keyMatch, error: keyError } = await supabase
        .from("competitions")
        .select("id")
        .eq("id", competition.id)
        .eq("registration_key", key)
        .maybeSingle();

      if (keyError) throw keyError;
      if (!keyMatch?.id) {
        notify("Invalid registration key.", "error");
        return;
      }

      const { error: joinError } = await supabase.from("user_selections").insert([
        {
          user_id: session.user.id,
          competition_id: competition.id,
          league: competition.league,
          season: competition.season,
          match_day: competition.start_match_day,
        },
      ]);

      if (joinError) {
        const alreadyRegistered =
          joinError.code === "23505" || (joinError.message || "").toLowerCase().includes("duplicate key");
        if (alreadyRegistered) {
          setIsRegistered(true);
          setShowPrivateKeyModal(false);
          setPrivateRegistrationKey("");
          notify("You are already registered for this competition.", "info");
          return;
        }
        throw joinError;
      }

      setIsRegistered(true);
      setShowPrivateKeyModal(false);
      setPrivateRegistrationKey("");
      notify("Successfully registered.", "success");
    } catch (e) {
      notify(e.message || "Failed to register.", "error");
    } finally {
      setRegistrationLoading(false);
    }
  };

  const handleUnregister = async () => {
    if (!session?.user?.id || !competition) {
      notify("You must be logged in.", "info");
      return;
    }

    try {
      setRegistrationLoading(true);
      const { error: removeError } = await supabase
        .from("user_selections")
        .delete()
        .eq("user_id", session.user.id)
        .eq("competition_id", competition.id);

      if (removeError) throw removeError;

      setIsRegistered(false);
      setPredictions({});
      setFormValues({});
      setShowUnregisterConfirm(false);
      notify("You have been unregistered.", "success");
    } catch (e) {
      notify(e.message || "Failed to unregister.", "error");
    } finally {
      setRegistrationLoading(false);
    }
  };

  const getFixtureState = (fixture) => {
    const confirmed = isConfirmedTeamName(fixture.home_team) && isConfirmedTeamName(fixture.away_team);
    const lockDeadline = getFixtureLockDeadline(fixture.kickoff_time);
    const locked = lockDeadline ? new Date() >= lockDeadline : false;
    const played = fixture.status === "complete";
    const editable =
      isRegistered &&
      competition?.status === "active" &&
      confirmed &&
      !locked &&
      !played &&
      fixture.status !== "cancelled" &&
      fixture.status !== "abandoned";

    return { confirmed, locked, played, editable, lockDeadline };
  };

  const hasSavedPredictions = useMemo(
    () => Object.keys(predictions || {}).length > 0,
    [predictions]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-canvas">
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <LoadingState label="Loading World Stage..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface-canvas">
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 p-8 fc-workspace">
          <Alert tone="danger">{error}</Alert>
          <Link to="/competitions" className="inline-block mt-4 text-action-primary underline">Back to Competitions</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-canvas">
      <div className="block md:hidden w-full bg-ui-green py-3 px-4 sticky top-0 z-40">
        <span className="text-white text-lg font-semibold">Footy Coach</span>
      </div>
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />

      <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 pb-24 md:pb-8 fc-workspace">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-brand-text">{competition?.name || "World Stage"}</h1>
          </div>
          <div className="w-fit flex flex-col items-end gap-2">
            {isRegistered ? (
              !hasSavedPredictions && (
              <FcButton variant="secondary" type="submit"
                onClick={() => setShowUnregisterConfirm(true)}
                disabled={registrationLoading}
                className="border"
              >
                {registrationLoading ? "Working..." : "Unregister"}
              </FcButton>
              )
            ) : (
              <FcButton variant="primary" type="submit"
                onClick={handleRegister}
                disabled={registrationLoading}
                className=""
              >
                {registrationLoading ? "Working..." : "Register to Predict"}
              </FcButton>
            )}
          </div>
        </div>

        <FcTabs label="World Stage sections" value={activeTab} onValueChange={setActiveTab} items={[{id:"predictions",label:<>
            <span>☑</span>
            Predictions
          </>},{id:"leaderboard",label:<>
            <span>🏆</span>
            Leaderboard
          </>},{id:"rules",label:<>
            <span>📋</span>
            Rules
          </>}]}>

        {activeTab === "predictions" && (
          <div className="space-y-6">
            {!isRegistered && (
              <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 p-3 rounded text-sm">
                You need to register for this competition before saving predictions.
              </div>
            )}

            <FcCard as="div" padding="standard" className="bg-surface-card rounded-xl border border-ui-border p-4 shadow-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-sm font-semibold text-content-primary">
                  Stage
                  <FcSelect aria-label="Stage"
                    value={predictionStageFilter}
                    onChange={(event) => setPredictionStageFilter(event.target.value)}
                    className="mt-1 block w-full border"
                  >
                    {predictionStageOptions.map((option) => (
                      <option key={option.key} value={option.key}>{option.label}</option>
                    ))}
                  </FcSelect>
                </label>
                <label className="text-sm font-semibold text-content-primary">
                  Group
                  <FcSelect aria-label="Group All Groups"
                    value={predictionGroupFilter}
                    onChange={(event) => setPredictionGroupFilter(event.target.value)}
                    className="mt-1 block w-full border"
                  >
                    <option value="ALL">All Groups</option>
                    {GROUP_LETTERS.map((group) => (
                      <option key={group} value={group}>Group {group}</option>
                    ))}
                  </FcSelect>
                </label>
              </div>
              <div className="mt-3 text-xs text-content-secondary">
                Showing {filteredFixtures.length} of {fixtures.length} fixtures
              </div>
            </FcCard>

            {filteredFixtures.length === 0 ? (
              <FcCard as="div" padding="standard" className="bg-surface-card rounded-xl border border-ui-border p-6 text-center text-content-secondary">
                No fixtures match the selected stage and group.
              </FcCard>
            ) : Object.entries(groupedFixtures).map(([dateLabel, rows]) => (
              <FcCard as="div" padding="none" key={dateLabel} className="bg-surface-card rounded-2xl border border-ui-border overflow-hidden shadow-sm">
                <div className="px-5 py-3 bg-gradient-to-r from-[#EAF3EE] to-[#F2F7F4] border-b border-ui-border text-lg font-semibold text-ui-green flex items-center gap-2">
                  <svg className="w-5 h-5 text-ui-green" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="16" rx="2" />
                    <path d="M16 3v4M8 3v4M3 10h18" />
                  </svg>
                  <span>{dateLabel}</span>
                </div>
                <div className="divide-y divide-gray-200">
                  {rows.map((fixture) => {
                    const state = getFixtureState(fixture);
                    const saved = predictions[fixture.id];
                    const displayHome = isConfirmedTeamName(fixture.home_team) ? getTeamDisplayName(fixture.home_team) : "TBC";
                    const displayAway = isConfirmedTeamName(fixture.away_team) ? getTeamDisplayName(fixture.away_team) : "TBC";
                    const homeLogo = getTeamLogo(displayHome);
                    const awayLogo = getTeamLogo(displayAway);
                    const currentHome = formValues[fixture.id]?.home ?? "";
                    const currentAway = formValues[fixture.id]?.away ?? "";
                    const syncStatus = fixtureSyncStatus[fixture.id] || "idle";

                    return (
                      <div key={fixture.id} className="p-5">
                        <div className="flex flex-col gap-4">
                          <div className="min-w-0">
                            <div className="text-sm text-content-secondary font-semibold tracking-wide mb-1 uppercase">
                              {getPredictionStageLabel(getPredictionStageKey(fixture.stage))}
                              {fixtureGroupMap[fixture.id] ? ` | Group ${fixtureGroupMap[fixture.id]}` : ""}
                            </div>
                            <div className="text-sm text-content-secondary mb-3">{formatFixtureKickoff(fixture.kickoff_time)}</div>
                            <div className="fc-score-fixture">
                              <div className="flex flex-col items-center gap-1 min-w-0 md:flex-row md:items-center md:gap-2 md:justify-self-end md:pr-3">
                                {homeLogo ? (
                                  <img src={homeLogo} alt={`${displayHome} badge`} className="w-10 h-10 rounded-full object-cover border border-ui-border shrink-0" />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-gray-200 border border-ui-border shrink-0" />
                                )}
                                <div className="font-semibold text-content-primary text-center md:text-left leading-tight whitespace-normal break-words max-w-[120px] md:max-w-[220px]">{displayHome}</div>
                              </div>
                              <ScoreControl label={displayHome} value={currentHome} displayValue={getScoreDisplay(currentHome)} disabled={!state.editable} onStep={delta => stepScore(fixture, 'home', delta)} />
                              <div className="px-3 py-1 rounded-lg bg-gray-100 text-content-secondary font-bold">VS</div>
                              <ScoreControl label={displayAway} value={currentAway} displayValue={getScoreDisplay(currentAway)} disabled={!state.editable} onStep={delta => stepScore(fixture, 'away', delta)} />
                              <div className="flex flex-col-reverse items-center justify-end gap-1 min-w-0 md:flex-row md:items-center md:gap-2 md:justify-self-start md:pl-3">
                                <div className="font-semibold text-content-primary text-center md:text-right leading-tight whitespace-normal break-words max-w-[120px] md:max-w-[220px]">{displayAway}</div>
                                {awayLogo ? (
                                  <img src={awayLogo} alt={`${displayAway} badge`} className="w-10 h-10 rounded-full object-cover border border-ui-border shrink-0" />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-gray-200 border border-ui-border shrink-0" />
                                )}
                              </div>
                            </div>
                            <div className="mt-2 flex items-center justify-end gap-1">
                              {state.played && <span className="px-2 py-0.5 rounded bg-green-100 text-ui-success">Played</span>}
                              {saved && state.played && (
                                <span className="px-2 py-0.5 rounded bg-blue-100 text-ui-green">+{saved.points_awarded || 0} pts</span>
                              )}
                            </div>
                            <div className="text-xs mt-1">
                              {!state.confirmed && <span className="px-2 py-0.5 rounded bg-gray-200 text-content-primary mr-2">Awaiting confirmation</span>}
                              {state.locked && !state.played && <span className="px-2 py-0.5 rounded bg-red-100 text-ui-error mr-2">Locked</span>}
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <StatusBadge tone={syncStatus === 'synced' ? 'success' : syncStatus === 'error' ? 'danger' : 'neutral'} role="status" aria-live="polite" aria-atomic="true">{getSyncLabel(syncStatus)}</StatusBadge>
                          </div>
                        </div>

                        {state.played && fixture.home_score != null && fixture.away_score != null && (
                          <div className="mt-2 text-sm text-content-primary">
                            Actual score: <span className="font-semibold">{fixture.home_score} - {fixture.away_score}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </FcCard>
            ))}
          </div>
        )}

        {showUnregisterConfirm && (
          <FcDialog open title="Confirm Unregister" size="standard" onCancel={() => setShowUnregisterConfirm(false)} cancelDisabled={registrationLoading}>
            <FcCard as="div" padding="standard" className="w-full max-w-md rounded-2xl bg-surface-card p-6 shadow-xl">

              <p className="text-sm text-content-primary mb-5">
                Are you sure you want to unregister from {competition?.name || "this competition"}?
              </p>
              <div className="flex justify-end gap-3">
                <FcButton variant="tertiary"
                  type="button"
                  onClick={() => setShowUnregisterConfirm(false)}
                  className=""
                  disabled={registrationLoading}
                >
                  Cancel
                </FcButton>
                <FcButton variant="primary"
                  type="button"
                  onClick={handleUnregister}
                  className=""
                  disabled={registrationLoading}
                >
                  {registrationLoading ? "Working..." : "Confirm"}
                </FcButton>
              </div>
            </FcCard>
          </FcDialog>
        )}

        {showPrivateKeyModal && (
          <FcDialog open title="Enter Registration Key" size="standard" onCancel={() => {
                    setShowPrivateKeyModal(false);
                    setPrivateRegistrationKey("");
                  }} cancelDisabled={registrationLoading}>
            <FcCard as="div" padding="standard" className="w-full max-w-md rounded-2xl bg-surface-card p-6 shadow-xl">

              <p className="text-sm text-content-primary mb-4">
                Enter the private key to register for {competition?.name || "this competition"}.
              </p>
              <FcInput aria-label="Private registration key"
                type="text"
                value={privateRegistrationKey}
                onChange={(e) => setPrivateRegistrationKey(e.target.value.toUpperCase())}
                placeholder="e.g. X4YH654"
                className="w-full border mb-5"
              />
              <div className="flex justify-end gap-3">
                <FcButton variant="tertiary"
                  type="button"
                  onClick={() => {
                    setShowPrivateKeyModal(false);
                    setPrivateRegistrationKey("");
                  }}
                  className=""
                  disabled={registrationLoading}
                >
                  Cancel
                </FcButton>
                <FcButton variant="primary"
                  type="button"
                  onClick={handleRegisterWithPrivateKey}
                  className=""
                  disabled={registrationLoading}
                >
                  {registrationLoading ? "Working..." : "Register"}
                </FcButton>
              </div>
            </FcCard>
          </FcDialog>
        )}

        {activeTab === "leaderboard" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-content-primary">Stage</label>
              <FcSelect aria-label="Stage"
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                className="border"
              >
                {leaderboardStageOptions.map((opt) => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </FcSelect>
            </div>

            {leaderboardLoading ? (
              <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg border border-ui-border p-4">Loading leaderboard...</FcCard>
            ) : leaderboard.length === 0 ? (
              <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg border border-ui-border p-4 text-content-secondary">No leaderboard data yet.</FcCard>
            ) : (
              <>
                <FcCard as="div" padding="none" className="bg-surface-card rounded-lg border border-ui-border overflow-hidden md:hidden">
                  <FcTable label="World Stage table 1" className="w-full text-left">
                    <thead className="bg-ui-green text-white">
                      <tr>
                        <th scope="col" className="p-2">Rank</th>
                        <th scope="col" className="p-2">Player</th>
                        <th scope="col" className="p-2 text-right">Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.map((row) => (
                        <tr key={row.user_id} className="border-t border-ui-border">
                          <td className="p-2">{row.rank}</td>
                          <td className="p-2">{row.username}</td>
                          <td className="p-2 text-right font-bold">{row.total_points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </FcTable>
                </FcCard>

                <FcCard as="div" padding="none" className="bg-surface-card rounded-lg border border-ui-border overflow-hidden hidden md:block">
                  <FcTable label="World Stage table 2" className="w-full text-left">
                    <thead className="bg-ui-green text-white">
                      <tr>
                        <th scope="col" className="p-2">Rank</th>
                        <th scope="col" className="p-2">Player</th>
                        <th scope="col" className="p-2 text-center">Selections</th>
                        <th scope="col" className="p-2 text-center">Played</th>
                        <th scope="col" className="p-2 text-center">Exact</th>
                        <th scope="col" className="p-2 text-center">Result</th>
                        <th scope="col" className="p-2 text-center">Incorrect</th>
                        <th scope="col" className="p-2 text-right">Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.map((row) => (
                        <tr key={row.user_id} className="border-t border-ui-border">
                          <td className="p-2">{row.rank}</td>
                          <td className="p-2">{row.username}</td>
                          <td className="p-2 text-center">{row.total_predictions_made}</td>
                          <td className="p-2 text-center">{row.total_predictions_played}</td>
                          <td className="p-2 text-center">{row.correct_scores}</td>
                          <td className="p-2 text-center">{row.correct_results}</td>
                          <td className="p-2 text-center">{row.incorrect_predictions}</td>
                          <td className="p-2 text-right font-bold">{row.total_points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </FcTable>
                </FcCard>
              </>
            )}
          </div>
        )}

        {activeTab === "rules" && (
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg border border-ui-border p-4">
            <h3 className="font-bold text-lg text-brand-text mb-3">World Stage Rules</h3>
            <ul className="text-sm text-content-primary space-y-2">
              <li>Predict each fixture scoreline individually.</li>
              <li>Exact score = 3 points, correct result = 1 point, otherwise 0 points.</li>
              <li>Predictions lock 1 hour before kickoff, per fixture.</li>
              <li>Only confirmed fixtures can be predicted. Future rounds will be added when both teams are confirmed.</li>
              <li>Scoring is based on 90-minute result only.</li>
            </ul>
          </FcCard>
        )}
</FcTabs>
      </div>
    </div>
  );
}
